import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import API from '../api/index.js';
import { API_URL } from '../api/api1.js';
import { DEMO_LOGIN_ENABLED } from '../demoMode.js';
import {
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  sendPlanAlert,
} from '../api/api1.js';
import { useLibrary } from '../context/LibraryContext.jsx';

const safeList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.notifications)) return res.notifications;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.notifications)) return res.data.notifications;
  if (Array.isArray(res.data?.data)) return res.data.data;
  return [];
};

const isNotificationUnread = (notification) => (
  notification.is_read === 0 || notification.is_read === '0' ||
  notification.read === false || notification.read === 0 || notification.read === '0' ||
  (notification.is_read == null && notification.read == null)
);

const getNotificationTone = (notification) => {
  const text = `${notification.title || ''} ${notification.message || ''}`.toLowerCase();
  if (/\b(exten(?:d|ded|sion))\b/.test(text)) return 'extension';
  if (/\b(expir(?:y|es|ed|ing))\b/.test(text)) return 'expiry';
  return '';
};

const BellIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
    <path d="M10 21h4" />
  </svg>
);

// Inline SVG icons used by the account menu.
const SunIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.5 15.5A8.5 8.5 0 0 1 8.5 3.5a8.5 8.5 0 1 0 12 12Z" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0 1 16 0" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5m0-8h.01" />
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2.75v9.5" />
    <path d="M7.1 5.65a9 9 0 1 0 9.8 0" />
  </svg>
);

const getInitials = (name = '') => {
  const clean = name.trim();
  if (!clean) return 'SA';
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export function AppHeader({ collapsed, setCollapsed, query, setQuery, selectedLibrary, setSelectedLibrary, libraryMenuOpen, setLibraryMenuOpen, profileMenuOpen, setProfileMenuOpen, setPage, onSignOut, toast, darkMode, setDarkMode, hideGlobalSearch = false }) {
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState('');
  const [photoVersion, setPhotoVersion] = useState(() => Date.now());
  const [notifOpen, setNotifOpen] = useState(false);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const [notifError, setNotifError] = useState('');
  const headerRef = useRef(null);
  const notifRef = useRef(null);
  const notifRequestRef = useRef(0);
  const { libraries } = useLibrary();

  const libraryOptions = useMemo(() => (Array.isArray(libraries) ? libraries : [])
    .map((library) => {
      if (typeof library === 'string') {
        return { value: library, name: library };
      }
      if (!library || typeof library !== 'object') return null;
      const name = library.library_name || library.libraryName || library.name || '';
      const value = library.id ?? library.library_id ?? library.libraryId ?? name;
      if (!String(name).trim() || value === null || value === undefined || !String(value).trim()) return null;
      return { value: String(value), name };
    })
    .filter((library) => library && !['all', 'all libraries'].includes(String(library.name).trim().toLowerCase())),
  [libraries]);

  const selectedLibraryOption = libraryOptions.find((library) => (
    String(library.value) === String(selectedLibrary) ||
    String(library.name).toLowerCase() === String(selectedLibrary).toLowerCase()
  ));
  const selectedLibraryLabel = !selectedLibrary || ['all', 'all libraries'].includes(String(selectedLibrary).toLowerCase())
    ? 'All Libraries'
    : selectedLibraryOption?.name || 'All Libraries';

  useEffect(() => {
    if (!selectedLibrary || ['all', 'all libraries'].includes(String(selectedLibrary).toLowerCase())) return;
    if (!libraryOptions.length) {
      setSelectedLibrary('all');
      return;
    }
    const selectionExists = libraryOptions.some((library) => (
      String(library.value) === String(selectedLibrary) ||
      String(library.name).toLowerCase() === String(selectedLibrary).toLowerCase()
    ));
    if (!selectionExists) setSelectedLibrary('all');
  }, [libraryOptions, selectedLibrary, setSelectedLibrary]);

  const { profileName, profileEmail } = (() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userinfo') || localStorage.getItem('userInfo') || '{}');
      const tenantInfo = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
      return {
        profileName: userInfo.name || userInfo.fullName || userInfo.username || tenantInfo.tenant_name || localStorage.getItem('userName') || 'Super Admin',
        profileEmail: userInfo.email || tenantInfo.email || localStorage.getItem('userEmail') || 'admin@studyroom.in'
      };
    } catch {
      return { profileName: 'Super Admin', profileEmail: 'admin@studyroom.in' };
    }
  })();

  const initials = useMemo(() => getInitials(profileName), [profileName]);

  useEffect(() => {
    const imageUrl = (value) => {
      if (typeof value !== 'string' || !value.trim()) return '';
      const path = value.trim();
      if (/^(https?:|blob:|data:)/i.test(path)) return path;
      const base = API_URL || API.defaults.baseURL || '';
      const normalized = path.startsWith('/') ? path : `/${path}`;
      return normalized.startsWith('/uploads/') ? `${base}/api${normalized}` : `${base}${normalized}`;
    };
    const readPhoto = (user) => user?.profile_photo || user?.profilePhoto || user?.photo_url || user?.avatar_url || user?.avatar || '';
    const cachedUser = (() => { try { return JSON.parse(localStorage.getItem('userinfo') || localStorage.getItem('userInfo') || '{}'); } catch { return {}; } })();
    setProfilePhoto(imageUrl(readPhoto(cachedUser)));
    setPhotoVersion(Date.now());

    let mounted = true;
    if (!DEMO_LOGIN_ENABLED) {
      API.get('/api/users/me').then(({ data }) => {
        const user = data?.user || data?.data?.user || data?.profile || data?.data || {};
        const photo = readPhoto(user);
        if (!mounted || !photo) return;
        const updated = { ...cachedUser, ...user };
        localStorage.setItem('userinfo', JSON.stringify(updated));
        setProfilePhoto(imageUrl(photo));
        setPhotoVersion(Date.now());
      }).catch(() => {});
    }

    const onProfileUpdated = (event) => {
      const updated = event.detail || (() => { try { return JSON.parse(localStorage.getItem('userinfo') || localStorage.getItem('userInfo') || '{}'); } catch { return {}; } })();
      setProfilePhoto(imageUrl(readPhoto(updated)));
      setPhotoVersion(Date.now());
    };
    const onStorageUpdated = (event) => {
      if (event.key !== 'userinfo' && event.key !== 'userInfo') return;
      try { setProfilePhoto(imageUrl(readPhoto(JSON.parse(event.newValue || '{}')))); setPhotoVersion(Date.now()); } catch {}
    };
    window.addEventListener('profileUpdated', onProfileUpdated);
    window.addEventListener('storage', onStorageUpdated);
    return () => { mounted = false; window.removeEventListener('profileUpdated', onProfileUpdated); window.removeEventListener('storage', onStorageUpdated); };
  }, []);

  const avatarPhotoUrl = useMemo(() => {
    if (!profilePhoto || /^(blob:|data:)/i.test(profilePhoto)) return profilePhoto;
    const separator = profilePhoto.includes('?') ? '&' : '?';
    return `${profilePhoto}${separator}v=${photoVersion}`;
  }, [profilePhoto, photoVersion]);

  const fetchNotifications = useCallback(async () => {
    const requestId = ++notifRequestRef.current;
    if (DEMO_LOGIN_ENABLED) { setNotifications([]); return; }
    setLoadingNotif(true);
    setNotifError('');
    try {
      const data = await getNotifications();
      if (requestId === notifRequestRef.current) setNotifications(safeList(data));
    } catch (e) {
      console.error('notif fetch failed', e);
      if (requestId === notifRequestRef.current) {
        setNotifError(e?.response?.data?.message || e?.message || 'Could not load notifications.');
      }
    } finally {
      if (requestId === notifRequestRef.current) setLoadingNotif(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 60000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setLibraryMenuOpen(false);
        setNotifOpen(false);
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [setLibraryMenuOpen, setProfileMenuOpen]);

  useEffect(() => {
    if (!selectedNotification) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelectedNotification(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNotification]);

  const unreadCount = useMemo(() => notifications.filter(isNotificationUnread).length, [notifications]);

  const handleNotifClick = async () => {
    const willOpen = !notifOpen;
    setNotifOpen(willOpen);
    setLibraryMenuOpen(false);
    setProfileMenuOpen(false);
    if (willOpen) await fetchNotifications();
  };

  const markAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: 1 })));
      toast?.('All notifications marked read');
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
      toast?.(error?.message || 'Could not mark notifications as read');
    }
  };

  const markRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      const markReadLocally = (notification) => (
        String(notification.id ?? notification.notification_id) === String(notificationId)
          ? { ...notification, read: true, is_read: 1 }
          : notification
      );
      setNotifications(prev => prev.map(markReadLocally));
      setSelectedNotification(current => current ? markReadLocally(current) : current);
    } catch (error) {
      console.error('Failed to mark notification as read', error);
      toast?.(error?.message || 'Could not mark notification as read');
    }
  };

  const sendNotificationAlert = async (studentId) => {
    try {
      await sendPlanAlert(studentId);
      toast?.('Alert sent');
    } catch (error) {
      console.error('Failed to send notification alert', error);
      toast?.(error?.message || 'Could not send alert');
    }
  };

  return <header className="topbar" ref={headerRef}>
    <button className="menu-button" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? 'Open navigation' : 'Collapse navigation'} aria-expanded={!collapsed}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
    </button>
    {!hideGlobalSearch && <label className="global-search"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.8" cy="10.8" r="6.8"/><path d="m16 16 4.5 4.5"/></svg><input aria-label="Search students and rooms" placeholder="Search students, rooms…" value={query} onChange={(event) => { setQuery(event.target.value); if (event.target.value) setPage('Students'); }} /></label>}

    <div className="top-actions">
      {libraryOptions.length > 1 && <div className="library-picker">
        <button className="library-select" aria-label="Choose library" aria-expanded={libraryMenuOpen} onClick={() => { setLibraryMenuOpen(!libraryMenuOpen); setProfileMenuOpen(false); setNotifOpen(false); }}>
          <svg className="library-building" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 21V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13M3 21h18M8 10h2m4 0h2m-8 4h2m4 0h2M4 6l8-3 8 3"/></svg><span>{selectedLibraryLabel}</span><span className="arrow" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="m4 6 4 4 4-4"/></svg></span>
        </button>
        {libraryMenuOpen && <div className="library-dropdown">
          <button className={!selectedLibraryOption ? 'selected' : ''} onClick={() => { setSelectedLibrary('all'); setLibraryMenuOpen(false); }}>
            <span><b>All Libraries</b></span>
            {!selectedLibraryOption && <i />}
          </button>
          {libraryOptions.map((library) => (
            <button key={library.value} className={selectedLibraryOption?.value === library.value ? 'selected' : ''} onClick={() => { setSelectedLibrary(library.value); setLibraryMenuOpen(false); }}>
              <span><b>{library.name}</b></span>
              {selectedLibraryOption?.value === library.value && <i />}
            </button>
          ))}
        </div>}
      </div>}

      <button className="icon-button theme-header-toggle" type="button" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} onClick={() => { setDarkMode(!darkMode); setLibraryMenuOpen(false); setNotifOpen(false); setProfileMenuOpen(false); }}>
        {darkMode ? <SunIcon /> : <MoonIcon />}
      </button>

      <div className="notif-wrapper" ref={notifRef}>
        <button className="icon-button bell" aria-label="Notifications" aria-expanded={notifOpen} onClick={handleNotifClick}>
          <svg className="topbar-bell-outline" viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>
          {unreadCount > 0 && <span className="notif-badge">{unreadCount > 9? '9+' : unreadCount}</span>}
        </button>

        {notifOpen && (
          <div className="notif-dropdown">
            <div className="notif-head">
              <b>Notifications ({notifications.length})</b>
              <div className="notif-head-actions">
                <button className="notif-link" onClick={fetchNotifications}>↻ Refresh</button>
                {unreadCount>0 && <button className="notif-link" onClick={markAllRead}>Mark all read</button>}
              </div>
            </div>
            <div className="notif-list" style={{maxHeight:400, overflowY:'auto'}}>
              {loadingNotif ? <div className="notif-empty">Loading...</div> : notifError && notifications.length === 0 ? (
                <div className="notif-empty"><p>Notifications unavailable</p><small>{notifError}</small></div>
              ) : notifications.length === 0? (
                <div className="notif-empty">
                  <BellIcon className="notif-empty-icon" />
                  <p>You’re all caught up</p>
                  <small>No notifications</small>
                </div>
              ) : (
                notifications.slice(0,10).map(n => {
                  const id = n.id ?? n.notification_id ?? n.student_id;
                  const unread = isNotificationUnread(n);
                  const isGeneralNotification = n.title !== undefined || n.message !== undefined;
                  const studentId = n.student_id ?? n.studentId;
                  const tone = getNotificationTone(n);
                  const title = n.title || n.student_name || n.name || 'Notification';
                  const detail = n.message || (
                    n.plan_status
                      ? (String(n.plan_status).toUpperCase() === 'EXPIRED'
                        ? 'Plan expired'
                        : `${n.days_left ?? ''} days left`)
                      : n.status || ''
                  );
                  return (
                    <div key={id ?? `${title}-${n.created_at || ''}`} className={`notif-item ${unread ? 'unread' : ''}`}>
                      <button type="button" className="notif-main" onClick={() => setSelectedNotification(n)}>
                        <span className="notif-icon">
                          {isGeneralNotification ? <BellIcon className={`notif-bell-${tone}`} /> : '⏰'}
                        </span>
                        <span className="notif-content">
                          <b>{title}</b>
                          <small className="notif-preview">{detail}</small>
                          <span className="notif-time">{n.created_at ? new Date(n.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short' }) : ''}</span>
                        </span>
                      </button>
                      {unread && id != null && isGeneralNotification
                        ? <button type="button" className="notif-link" onClick={() => markRead(id)}>Mark read</button>
                        : null}
                      {!isGeneralNotification && studentId != null
                        ? <button type="button" className="notif-link" onClick={() => sendNotificationAlert(studentId)}>Send alert</button>
                        : null}
                      {unread && <i className="unread-dot" />}
                    </div>
                  );
                })
              )}
            </div>
            <div className="notif-foot">
              <button onClick={()=>{ setNotifOpen(false); setPage('Settings'); }}>View all notifications</button>
            </div>
          </div>
        )}
      </div>
      {selectedNotification && (() => {
        const detailTitle = selectedNotification.title || selectedNotification.student_name || selectedNotification.name || 'Notification';
        const detailMessage = selectedNotification.message || (
          selectedNotification.plan_status
            ? (String(selectedNotification.plan_status).toUpperCase() === 'EXPIRED'
              ? 'Plan expired'
              : `${selectedNotification.days_left ?? ''} days left`)
            : selectedNotification.status || ''
        );
        const notificationId = selectedNotification.id ?? selectedNotification.notification_id;
        const unread = isNotificationUnread(selectedNotification);
        const tone = getNotificationTone(selectedNotification);
        return createPortal(
          <div
            className="notification-detail-overlay"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSelectedNotification(null);
            }}
          >
            <section
              className={`notification-detail-modal notification-detail-${String(selectedNotification.type || 'info').toLowerCase()}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="notification-detail-title"
            >
              <header className="notification-detail-header">
                <span className={`notification-detail-icon notification-detail-icon-${tone}`}><BellIcon /></span>
                <div>
                  <small>Notification</small>
                  <h2 id="notification-detail-title">{detailTitle}</h2>
                </div>
                <button
                  type="button"
                  className="notification-detail-close"
                  aria-label="Close notification"
                  onClick={() => setSelectedNotification(null)}
                >×</button>
              </header>
              <div className="notification-detail-body">
                <p>{detailMessage || 'No message provided.'}</p>
                <div className="notification-detail-meta">
                  {selectedNotification.sender_type && (
                    <span className="notification-sender">{selectedNotification.sender_type}</span>
                  )}
                  {selectedNotification.created_at && (
                    <time dateTime={selectedNotification.created_at}>
                      {new Date(selectedNotification.created_at).toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </time>
                  )}
                </div>
              </div>
              <footer className="notification-detail-footer">
                {unread && notificationId != null && (
                  <button type="button" className="notification-detail-read" onClick={() => markRead(notificationId)}>
                    Mark as read
                  </button>
                )}
                <button type="button" className="notification-detail-dismiss" onClick={() => setSelectedNotification(null)}>
                  Close
                </button>
              </footer>
            </section>
          </div>,
          document.body
        );
      })()}

      <div className="profile-picker">
        <button className="profile" aria-label={`Account menu for ${profileName}`} aria-expanded={profileMenuOpen} onClick={() => { setProfileMenuOpen(!profileMenuOpen); setLibraryMenuOpen(false); setNotifOpen(false); }}>
          <span className="avatar">{avatarPhotoUrl ? <img src={avatarPhotoUrl} alt="" onError={() => setProfilePhoto('')} /> : initials}</span>
          <span className="arrow" aria-hidden="true"><svg viewBox="0 0 16 16"><path d="m4 6 4 4 4-4"/></svg></span>
        </button>
        {profileMenuOpen && <div className="profile-dropdown">
          <div className="header-profile-identity">
            <span className="header-profile-identity-avatar">{avatarPhotoUrl ? <img src={avatarPhotoUrl} alt="" onError={() => setProfilePhoto('')} /> : initials}</span>
            <span className="header-profile-identity-copy"><b>{profileName}</b><small>{profileEmail}</small></span>
          </div>
          <button
            type="button"
            className="profile-theme-option"
            onClick={() => { setDarkMode(!darkMode); setProfileMenuOpen(false); }}
          >
            <span
              className="profile-menu-icon"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, flexShrink: 0 }}
            >{darkMode ? <SunIcon /> : <MoonIcon />}</span>
            {darkMode ? 'Light mode' : 'Dark mode'}
          </button>
          <button type="button" onClick={() => { setPage('Profile'); setProfileMenuOpen(false); }}>
            <span
              className="profile-menu-icon"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, flexShrink: 0 }}
            ><UserIcon /></span>
            My Profile
          </button>
          <button type="button" onClick={() => { setPage('Settings'); setProfileMenuOpen(false); }}>
            <span
              className="profile-menu-icon"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, flexShrink: 0 }}
            ><SettingsIcon /></span>
            Settings
          </button>
          <button type="button" onClick={() => { setPage('Setup Guide'); setProfileMenuOpen(false); }}>
            <span
              className="profile-menu-icon"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, flexShrink: 0 }}
            ><InfoIcon /></span>
            Setup Guide
          </button>
          <button
            type="button"
            className="profile-logout"
            onClick={() => { onSignOut(); setProfileMenuOpen(false); }}
          >
            <span
              className="profile-menu-icon"
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, flexShrink: 0 }}
            ><LogoutIcon /></span>
            Logout
          </button>
        </div>}
      </div>
    </div>
  </header>;
}
